'use client'

import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Pencil, Send, Trash } from 'lucide-react'

import type { BookDetails } from '@package/api'
import type { RenderLink } from '@ui/web/utils'
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Skeleton,
  toast,
} from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { getStaticAssetUrl } from '@helper/utils/file'
import cn from '@config/tailwind/cn'

import type { CardAction } from '../components'
import { BookGalleryCard } from '../components'
import { useSharedTRPC } from '../context'
import { MyBookCollectionForm } from '../forms'
import { ShareContentDialog } from '../lib/share-content-dialog'
import { BookCollectionDetailView } from './book-collection-detail-view'
import { DeleteBookCollectionModal } from './my-book-collection/my-book-collection/delete-modal'

/** Joins author names in Indonesian list style: "A", "A dan B", "A, B, dan C". */
function formatAuthorNames(names: string[]): string | undefined {
  const clean = names.filter(Boolean)
  if (clean.length === 0) return undefined
  if (clean.length === 1) return clean[0]
  if (clean.length === 2) return `${clean[0]} dan ${clean[1]}`
  return `${clean.slice(0, -1).join(', ')}, dan ${clean[clean.length - 1]}`
}

interface MyBookCollectionDetailPageProps {
  /** Owner view: the collection loaded through `getOneBookCollection`, which is owner-scoped. */
  myBookCollectionId?: string
  /**
   * Recipient view: the same collection loaded through `getSharedCollection`, which only
   * answers for a viewer holding a live, unrevoked share grant. Read-only — edit, delete
   * and re-share belong to the owner alone, so those CTAs are suppressed regardless of
   * `withShareCta` / `onSuccessDeleteBookCollection`. Mutually exclusive with
   * `myBookCollectionId`.
   */
  sharedCollectionId?: string
  /**
   * Rendered when the query resolves empty — the collection is gone, or (shared view) the
   * viewer holds no grant for it. Without it the page renders nothing, which reads as a
   * broken screen rather than a refusal.
   */
  notFound?: React.ReactNode
  backLabel?: string
  renderBackLink: RenderLink
  renderBookAction: (book: BookDetails) => CardAction
  renderMakeNewChildLink: RenderLink
  /** Links used by the empty state when the collection has no books. */
  renderExplorePage: RenderLink
  renderSearchPage: RenderLink
  /** Name shown as "Disusun oleh: …" after the book count. */
  compiledBy?: string
  withShareCta?: boolean
  onSuccessDeleteBookCollection?: () => void
  onTagClick?: (type: 'author' | 'publisher' | 'level', id: string) => void
  className?: string
}

export function MyBookCollectionDetailPage({
  myBookCollectionId,
  sharedCollectionId,
  notFound,
  backLabel = 'Koleksiku',
  renderBackLink,
  renderBookAction,
  renderMakeNewChildLink,
  renderExplorePage,
  renderSearchPage,
  compiledBy,
  withShareCta = false,
  onSuccessDeleteBookCollection,
  onTagClick,
  className,
}: MyBookCollectionDetailPageProps) {
  if (!myBookCollectionId && !sharedCollectionId) {
    throw new Error('Either myBookCollectionId or sharedCollectionId must be provided')
  }

  const trpc = useSharedTRPC()
  const queryClient = useQueryClient()

  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [openEditDialog, setOpenEditDialog] = useState(false)
  const [openShareDialog, setOpenShareDialog] = useState(false)

  const isShared = Boolean(sharedCollectionId)

  // Two queries rather than one options ternary: the shared row is narrower (no
  // `sentBookCollections`, plus an `ownerName`), and mixing the two option objects in a
  // ternary makes TanStack Query's generics unresolvable.
  const ownedQuery = useQuery({
    ...trpc.myBookCollection.getOneBookCollection.queryOptions({ id: myBookCollectionId }),
    enabled: !isShared,
  })

  const sharedQuery = useQuery({
    ...trpc.myBookCollection.getSharedCollection.queryOptions({ collectionId: sharedCollectionId ?? '' }),
    enabled: isShared,
  })

  const { data, isLoading } = isShared ? sharedQuery : ownedQuery
  // Owner-only dialogs read this, never `data`: it is the only branch whose row carries
  // everything they mutate, and it is `undefined` in the shared view by construction.
  const ownedData = isShared ? undefined : ownedQuery.data
  const resolvedCompiledBy = compiledBy ?? (isShared ? sharedQuery.data?.ownerName : undefined)

  if (isLoading) {
    return (
      <main className={cn('srd:w-full srd:h-full srd:min-h-dvh', className)}>
        <div className='srd:w-full srd:max-w-[1200px] srd:mx-auto srd:flex srd:flex-col srd:gap-y-10 srd:px-4 srd:py-10'>
          <Skeleton className='srd:h-5 srd:w-32' />
          <div className='srd:grid srd:md:grid-cols-[auto_1fr] srd:grid-cols-1 srd:gap-x-6 srd:gap-y-6'>
            <Skeleton className='srd:aspect-square srd:w-[280px] srd:rounded-lg' />
            <div className='srd:flex srd:flex-col srd:gap-y-4'>
              <Skeleton className='srd:h-8 srd:w-3/4' />
              <Skeleton className='srd:h-5 srd:w-20' />
              <Skeleton className='srd:h-16 srd:w-full' />
            </div>
          </div>
          <Skeleton className='srd:h-[208px] srd:w-full srd:rounded-lg' />
        </div>
      </main>
    )
  }

  if (!data) return <>{notFound ?? null}</>

  const activeBooks = data.myBookCollectionToBooks.filter((v) => v.book.isActive)

  return (
    <>
      <BookCollectionDetailView
        className={className}
        books={activeBooks}
        name={data.name}
        description={data.description}
        compiledBy={resolvedCompiledBy}
        backLabel={backLabel}
        renderBackLink={renderBackLink}
        renderBookAction={renderBookAction}
        onTagClick={onTagClick}
        cover={<BookGalleryCard collection={activeBooks} withAnimation={false} className='srd:w-full' />}
        emptyState={
          <div className='srd:flex srd:flex-col srd:items-center srd:justify-center srd:min-h-[40dvh] srd:text-center srd:py-10 srd:text-neutral-white srd:gap-y-6 srd:max-w-[800px] srd:mx-auto'>
            <h6 className={cn(typographyVariants({ variant: 'title-md' }))}>
              {isShared ? 'Koleksi ini masih kosong' : 'Belum ada yang disimpan'}
            </h6>

            <img
              src={getStaticAssetUrl('illustration/foam.png')}
              alt=''
              draggable={false}
              className='srd:w-auto srd:h-auto srd:max-h-32'
            />

            <p className={cn(typographyVariants({ variant: 'body-lg' }))}>
              {isShared
                ? 'Belum ada buku di dalam koleksi yang dibagikan ini. Sementara itu, kamu bisa cari bacaan lain di bawah.'
                : 'Ada buku atau pelajaran yang kamu sukai? Simpan di perpustakaan pribadimu di sini agar mudah kalau kamu ingin lihat lagi.'}
            </p>

            <div className='srd:flex srd:flex-col srd:items-center srd:justify-center srd:gap-y-2'>
              <Button asChild>{renderExplorePage({ children: 'Jelajahi beranda' })}</Button>
              <Button asChild variant='outline' className='srd:text-neutral-white'>
                {renderSearchPage({ children: 'Cari berdasarkan kategori' })}
              </Button>
            </div>
          </div>
        }
        cta={
          // The recipient view owns nothing here: every control below mutates or re-shares
          // the owner's collection.
          isShared ? undefined : (
            <div className='srd:flex srd:flex-wrap srd:items-center srd:gap-x-2 srd:gap-y-2'>
              {withShareCta && (
                <Button
                  variant='ghost'
                  onClick={() => setOpenShareDialog(true)}
                  className='srd:w-fit srd:gap-x-2 srd:px-2 srd:text-neutral-white srd:hover:bg-transparent srd:hover:opacity-70'
                >
                  <Send className='srd:size-4' />
                  Bagikan
                </Button>
              )}

              <Button
                variant='ghost'
                onClick={() => setOpenEditDialog(true)}
                className='srd:w-fit srd:gap-x-2 srd:px-2 srd:text-neutral-white srd:hover:bg-transparent srd:hover:opacity-70'
              >
                <Pencil className='srd:size-4' />
                Edit
              </Button>

              {onSuccessDeleteBookCollection && (
                <Button
                  variant='ghost'
                  onClick={() => setOpenDeleteDialog(true)}
                  className='srd:w-fit srd:gap-x-2 srd:px-2 srd:text-main-red-100 srd:hover:bg-transparent srd:hover:opacity-70'
                >
                  <Trash className='srd:size-4' />
                  Hapus
                </Button>
              )}
            </div>
          )
        }
      />

      {ownedData && (
        <Dialog open={openEditDialog} onOpenChange={setOpenEditDialog} modal>
          <DialogContent className='srd:max-h-[85dvh] srd:w-full srd:min-w-0 srd:max-w-[500px] srd:overflow-x-hidden srd:overflow-y-auto'>
            <DialogHeader>
              <DialogTitle>Edit kompilasiku</DialogTitle>
              <DialogDescription className='srd:sr-only'>
                Ubah nama, deskripsi, dan urutan buku dalam kompilasi ini.
              </DialogDescription>
            </DialogHeader>

            <MyBookCollectionForm
              withAreYouSureDialog
              initialData={{
                myBookCollectionId: ownedData.id,
                name: ownedData.name,
                description: ownedData.description ?? undefined,
              }}
              books={ownedData.myBookCollectionToBooks.map((row) => ({
                bookId: row.book.id,
                title: row.book.title,
                author: formatAuthorNames(row.book.authors.map((a) => a.name)),
                coverUrl: row.book.coverUrl,
              }))}
              onFinishSubmitting={async (updated) => {
                toast.success('Koleksi berhasil diperbarui!')
                await queryClient.invalidateQueries({
                  queryKey: trpc.myBookCollection.getOneBookCollection.queryKey({ id: updated.id }),
                })
                setOpenEditDialog(false)
              }}
              onCancel={() => setOpenEditDialog(false)}
            />
          </DialogContent>
        </Dialog>
      )}

      {ownedData && onSuccessDeleteBookCollection && (
        <DeleteBookCollectionModal
          data={ownedData}
          open={openDeleteDialog}
          onOpenChange={setOpenDeleteDialog}
          onSuccess={onSuccessDeleteBookCollection}
        />
      )}

      {ownedData && withShareCta && (
        <ShareContentDialog
          open={openShareDialog}
          onOpenChange={setOpenShareDialog}
          entityType='COLLECTION'
          entityId={ownedData.id}
          cover={{ kind: 'collection', books: activeBooks }}
          renderAddChildLink={renderMakeNewChildLink}
          onSuccess={async () => {
            await queryClient.invalidateQueries({
              queryKey: trpc.myBookCollection.getOneBookCollection.queryKey({ id: ownedData.id }),
            })
          }}
        />
      )}
    </>
  )
}
